import React from 'react'
import './index.styl'

// Keep in sync with public/privacy-policy.html (static copy served to crawlers that do not run JS)
const Privacy = () => {
  return (
    <div className='help_container'>
      <h1 className='pageTitle'>
        Privacy Policy
      </h1>
      <div className='textBlock'>
        <p>
          Last Updated: <b className='boldText'>October 13, 2026</b>
        </p>
      </div>
      <h2 className='chapterTitle'>
        Introduction
      </h2>
      <div className='textBlock'>
        <p>
          This Privacy Policy explains how Ticket Lyfe (“Ticket Lyfe”, “we”, “us”) collects, uses, shares and protects personal information when you use our website, our ticketing, live stream, virtual meeting and membership services, and any related features (together, the “Services”). By using the Services you agree to the practices described here. This policy should be read together with our Terms and Conditions.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Information We Collect
      </h2>
      <div className='textBlock'>
        <p>
          We collect the following categories of information:
        </p>
        <ul className='disc'>
          <li>
            <strong className='boldText'>Account information:</strong> your name, username, email address, mobile phone number, mailing address and password (stored in hashed form). Guests who buy tickets without an account provide an email address and mobile number.
          </li>
          <li>
            <strong className='boldText'>Sign-in with Google:</strong> if you choose to sign in or sign up with Google, we receive your Google account email address, name and profile picture. See “Google User Data” below.
          </li>
          <li>
            <strong className='boldText'>Ticket and event information:</strong> events you create, tickets you make, buy, sell, gift or are assigned, seat selections, box office and sales records, and content you add to events or memberships (descriptions, images, chat messages, announcements).
          </li>
          <li>
            <strong className='boldText'>Payment and payout information:</strong> payments are processed by Stripe, PayPal and Venmo. Card details are entered with and held by those providers; we do not store full card numbers. We keep transaction records, and for Promoters and Venues the payout details required to pay you (such as a connected Stripe account or bank account information).
          </li>
          <li>
            <strong className='boldText'>Technical information:</strong> IP address, browser and device type, pages viewed, approximate location derived from IP, and cookies or similar technologies.
          </li>
          <li>
            <strong className='boldText'>Communications:</strong> messages you send to our support team and your notification preferences.
          </li>
        </ul>
      </div>
      <h2 className='chapterTitle'>
        How We Use Information
      </h2>
      <div className='textBlock'>
        <ul className='disc'>
          <li>
            To create and secure your account and let you sign in.
          </li>
          <li>
            To provide the Services: make, sell, buy, gift, deliver and scan tickets, run live streams, virtual meetings and memberships, and process payments and payouts.
          </li>
          <li>
            To send transactional messages such as ticket confirmations, receipts, event updates and security notices by email and SMS.
          </li>
          <li>
            To track ticket sales for Promoters, Co-Promoters, Sub-Promoters and Venues, as described below.
          </li>
          <li>
            To prevent fraud and abuse, enforce our Terms and Conditions, and comply with legal obligations.
          </li>
          <li>
            To maintain, analyze and improve the Services.
          </li>
        </ul>
        <p>
          We do not sell your personal information.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Google User Data
      </h2>
      <div className='textBlock'>
        <p>
          If you use Sign in with Google, Ticket Lyfe accesses only your Google account email address, name and profile picture. We use this information solely to create your Ticket Lyfe account, identify you and let you sign in. We do not use Google user data for advertising, we do not sell it, we do not transfer it to third parties except as needed to provide the Services (for example, our hosting and email providers acting on our behalf) or as required by law, and we do not allow humans to read it except with your consent, for security purposes, or to comply with law.
        </p>
        <p>
          Ticket Lyfe’s use and transfer of information received from Google APIs adheres to the <a href='https://developers.google.com/terms/api-services-user-data-policy' target='_blank' rel='noopener noreferrer'>Google API Services User Data Policy</a>, including the Limited Use requirements. You can revoke Ticket Lyfe’s access at any time in your Google Account permissions settings, and you can ask us to delete your account and data as described below.
        </p>
      </div>
      <h2 className='chapterTitle'>
        How We Share Information
      </h2>
      <div className='textBlock'>
        <ul className='disc'>
          <li>
            <strong className='boldText'>Promoters and Venues:</strong> when you buy or are gifted a ticket, your Ticket Lyfe username, email address and mobile phone number are shared with the Promoter, Co-Promoter, Sub-Promoter or Venue selling or gifting the ticket, for the purpose of tracking ticket sales. Our Terms require them not to use this information for any other purpose or share it with other third parties.
          </li>
          <li>
            <strong className='boldText'>Service providers acting on our behalf:</strong> Stripe, PayPal and Venmo (payments and payouts), Mailjet (email), Twilio (SMS), Amazon Web Services (hosting, file storage and key management), Sentry (error monitoring), Google Tag Manager and analytics tools, and Cloudflare (bot protection). They may only use the information to provide services to us.
          </li>
          <li>
            <strong className='boldText'>Legal and safety:</strong> when required by law or legal process, or to protect the rights, property or safety of Ticket Lyfe, our users or the public.
          </li>
          <li>
            <strong className='boldText'>Business transfers:</strong> in connection with a merger, acquisition or sale of assets, in which case the information would remain subject to this policy.
          </li>
        </ul>
      </div>
      <h2 className='chapterTitle'>
        Cookies and Analytics
      </h2>
      <div className='textBlock'>
        <p>
          We use cookies and similar technologies to keep you signed in, remember preferences, protect against abuse and understand how the Services are used. We use Google Tag Manager and related analytics tools for this purpose. You can control cookies through your browser settings; disabling them may affect parts of the Services.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Data Retention and Deletion
      </h2>
      <div className='textBlock'>
        <p>
          We keep personal information for as long as your account is active and as needed to provide the Services, resolve disputes, and meet legal, tax and accounting obligations (for example, transaction and payout records). You may request deletion of your account and personal data at any time by emailing <a href='mailto:support@ticketlyfe.com'>support@ticketlyfe.com</a> from the email address on your account. Our support team reviews and processes these requests manually, and we will delete or anonymize your data within a reasonable period, except for information we are required or permitted to keep by law.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Your Choices and Rights
      </h2>
      <div className='textBlock'>
        <p>
          You can review and update your profile information in your account. Depending on where you live, you may have the right to access, correct, delete or export your personal information, or to object to or restrict certain processing. To exercise these rights, contact us at <a href='mailto:support@ticketlyfe.com'>support@ticketlyfe.com</a>. You can opt out of marketing messages by following the unsubscribe instructions in them, and reply STOP to opt out of SMS messages; transactional messages about your tickets and account will still be sent.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Security
      </h2>
      <div className='textBlock'>
        <p>
          We use reasonable technical and organizational measures to protect personal information, including encrypted connections and hashed passwords. No method of transmission or storage is completely secure, so we cannot guarantee absolute security. You are responsible for keeping your login credentials confidential.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Children’s Privacy
      </h2>
      <div className='textBlock'>
        <p>
          Ticket Lyfe accounts are intended for adults. Events on Ticket Lyfe may be organized for children, such as youth sports clubs, but tickets and accounts for those events must be created and managed by a parent or legal guardian. We do not knowingly collect personal information directly from children under 13. If you believe a child has provided us personal information without parental consent, contact us at <a href='mailto:support@ticketlyfe.com'>support@ticketlyfe.com</a> and we will delete it.
        </p>
      </div>
      <h2 className='chapterTitle'>
        International Users
      </h2>
      <div className='textBlock'>
        <p>
          Ticket Lyfe is operated from the United States. If you use the Services from outside the United States, your information will be transferred to, stored and processed in the United States, where data protection laws may differ from those in your country.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Changes to This Policy
      </h2>
      <div className='textBlock'>
        <p>
          We may update this Privacy Policy from time to time. The revised version will be posted on this page with a new “Last Updated” date and is effective when posted. If changes are material, we will take reasonable steps to notify you.
        </p>
      </div>
      <h2 className='chapterTitle'>
        Contact Us
      </h2>
      <div className='textBlock'>
        <p>
          Questions or requests about this Privacy Policy or your personal information: <a href='mailto:support@ticketlyfe.com'>support@ticketlyfe.com</a>.
        </p>
      </div>
    </div>
  )
}

export default Privacy
